import type { DriveManagementCopy } from '@/i18n/drives-management-copy-types';

export const enDriveManagementCopy = {
  description: 'Shared files for teams, projects, and collaborators.',
  createDrive: 'Create Drive',
  createDriveTitle: 'Create a Shared Drive',
  driveName: 'Drive name',
  driveNameHint:
    'Drive names are unique and become their readable folder path.',
  driveNameTaken: 'A Drive named “{name}” already exists.',
  driveNameReserved: '“{name}” is reserved. Choose another name.',
  driveNameInvalid: 'Use letters or numbers in the Drive name.',
  driveNameSuggestions: 'Available:',
  driveDescription: 'Description (optional)',
  governance: 'Drive for',
  curatorOptIn: 'Make me a Library Manager',
  workspace: 'Workspace',
  teamGoverned: 'Team',
  projectGoverned: 'Project',
  selectTeam: 'Select a team',
  selectProject: 'Select a project',
  projectVisibility: 'Project visibility',
  assignedMembers: 'Assigned members',
  workspaceVisible: 'Workspace-visible',
  customAudience: 'Custom audience',
  primaryProjectDrive: 'Make this the primary project Drive',
  create: 'Create',
  cancel: 'Cancel',
  loading: 'Loading Drives…',
  emptyTitle: 'No Drives available',
  emptyDescription: 'Create a Shared Drive or ask a Manager for access.',
  openDrive: 'Open Drive',
  settings: 'Drive settings',
  members: 'Members',
  links: 'Links',
  permissions: 'Permissions',
  workflow: 'Workflow',
  activity: 'Activity',
  storage: 'Storage',
  role: 'Role',
  status: 'Status',
  usage: 'Storage used',
  restrictedFolders: 'Restricted folders',
  guestManagement: 'Guest management',
  createFailed: 'Could not create the Drive.',
  addMember: 'Add member',
  removeMember: 'Remove',
  selectMember: 'Select a workspace member',
  noDirectMembers: 'No direct members yet.',
  unknownMember: 'Unknown member',
  loadMoreMembers: 'Load more members',
  memberAdded: 'Member added.',
  memberRemoved: 'Member removed.',
  membersFailed: 'Could not update Drive members.',
  managerOnly: 'Only Drive Managers can manage members.',
  linkType: 'Link type',
  selectLinkTarget: 'Select a team or project',
  addLink: 'Add link',
  removeLink: 'Remove',
  noLinks: 'This Drive has no team or project links.',
  unknownLink: 'Unavailable context',
  linkAdded: 'Drive link added.',
  linkRemoved: 'Drive link removed.',
  linksFailed: 'Could not update Drive links.',
  viewerDownloads: 'Allow Viewers to download',
  publicLinksWorkspacePolicy:
    'Public links remain controlled by the workspace owner policy.',
  selectFolder: 'Select a folder',
  restrict: 'Restrict',
  removeRestriction: 'Remove boundary',
  boundaryCreated: 'Restricted-folder boundary created.',
  boundaryRemoved: 'Restricted-folder boundary removed.',
  createWorkflow: 'Create default workflow',
  workflowCreated: 'Default workflow created.',
  noWorkflows: 'No workflow is configured for this Drive.',
  noActivity: 'No Drive activity yet.',
  activityAccess: 'Access changed',
  activityContent: 'Content activity',
  activityWorkflow: 'Workflow activity',
  activityPublish: 'Publishing activity',
  activityGovernance: 'Drive settings updated',
  activityRecovery: 'Recovery activity',
  activitySecurity: 'Security activity',
  activityAutomation: 'Agent activity',
  activityDrive: 'Drive updated',
  policyUpdated: 'Drive policy updated.',
  settingsFailed: 'Could not update Drive settings.',
  guestEmail: 'Guest account email',
  addGuest: 'Add guest',
  guestAdded: 'Guest added as Commenter.',
  guestFailed:
    'Could not add this guest. They must already have an IMAI identity.',
  guestExpiry:
    'Guest access expires after 90 days by default and is capped at Contributor.',
  recovery: 'Recovery',
  contentAccessRequired: 'Content access required',
  recoveryDescription:
    'Assign a replacement Manager without opening or viewing Drive content.',
  replacementManager: 'Select a replacement Manager',
  assignManager: 'Assign Manager',
  recoveryAssigned: 'Replacement Manager assigned.',
  recoveryFailed: 'Could not assign the replacement Manager.',
  publishToLibrary: 'Publish to workspace Drive',
  publish: 'Publish',
  publishStarted: 'Managed Library copy started.',
  publishFailed: 'Could not publish this file.',
  noPublishableFiles: 'This Drive has no files to publish.',
  libraryCuratorRequired:
    'Library Content Manager access is required to publish.',
} satisfies DriveManagementCopy;

export const arDriveManagementCopy = {
  description: 'ملفات مشتركة للفرق والمشاريع والمتعاونين.',
  createDrive: 'إنشاء محرك أقراص',
  createDriveTitle: 'إنشاء محرك أقراص مشترك',
  driveName: 'اسم محرك الأقراص',
  driveNameHint: 'أسماء محركات الأقراص فريدة وتصبح مسار المجلد المقروء.',
  driveNameTaken: 'يوجد محرك أقراص باسم «{name}» بالفعل.',
  driveNameReserved: 'الاسم «{name}» محجوز. اختر اسمًا آخر.',
  driveNameInvalid: 'استخدم أحرفًا أو أرقامًا في اسم محرك الأقراص.',
  driveNameSuggestions: 'متاح:',
  driveDescription: 'الوصف (اختياري)',
  governance: 'محرك الأقراص مخصص لـ',
  curatorOptIn: 'تعييني مديرًا للمكتبة',
  workspace: 'مساحة العمل',
  teamGoverned: 'الفريق',
  projectGoverned: 'المشروع',
  selectTeam: 'اختر فريقًا',
  selectProject: 'اختر مشروعًا',
  projectVisibility: 'ظهور المشروع',
  assignedMembers: 'الأعضاء المعيّنون',
  workspaceVisible: 'مرئي لمساحة العمل',
  customAudience: 'جمهور مخصص',
  primaryProjectDrive: 'اجعل هذا محرك الأقراص الأساسي للمشروع',
  create: 'إنشاء',
  cancel: 'إلغاء',
  loading: 'جارٍ تحميل محركات الأقراص…',
  emptyTitle: 'لا توجد محركات أقراص متاحة',
  emptyDescription: 'أنشئ محركًا مشتركًا أو اطلب الوصول من أحد المديرين.',
  openDrive: 'فتح محرك الأقراص',
  settings: 'إعدادات محرك الأقراص',
  members: 'الأعضاء',
  links: 'الروابط',
  permissions: 'الأذونات',
  workflow: 'سير العمل',
  activity: 'النشاط',
  storage: 'التخزين',
  role: 'الدور',
  status: 'الحالة',
  usage: 'مساحة التخزين المستخدمة',
  restrictedFolders: 'المجلدات المقيّدة',
  guestManagement: 'إدارة الضيوف',
  createFailed: 'تعذّر إنشاء محرك الأقراص.',
  addMember: 'إضافة عضو',
  removeMember: 'إزالة',
  selectMember: 'اختر عضوًا من مساحة العمل',
  noDirectMembers: 'لا يوجد أعضاء مباشرون بعد.',
  unknownMember: 'عضو غير معروف',
  loadMoreMembers: 'تحميل المزيد من الأعضاء',
  memberAdded: 'تمت إضافة العضو.',
  memberRemoved: 'تمت إزالة العضو.',
  membersFailed: 'تعذّر تحديث أعضاء محرك الأقراص.',
  managerOnly: 'يمكن لمديري محرك الأقراص فقط إدارة الأعضاء.',
  linkType: 'نوع الرابط',
  selectLinkTarget: 'اختر فريقًا أو مشروعًا',
  addLink: 'إضافة رابط',
  removeLink: 'إزالة',
  noLinks: 'لا يرتبط محرك الأقراص هذا بأي فريق أو مشروع.',
  unknownLink: 'سياق غير متاح',
  linkAdded: 'تمت إضافة رابط محرك الأقراص.',
  linkRemoved: 'تمت إزالة رابط محرك الأقراص.',
  linksFailed: 'تعذّر تحديث روابط محرك الأقراص.',
  viewerDownloads: 'السماح للمشاهدين بالتنزيل',
  publicLinksWorkspacePolicy:
    'تظل الروابط العامة خاضعة لسياسة مالك مساحة العمل.',
  selectFolder: 'اختر مجلدًا',
  restrict: 'تقييد',
  removeRestriction: 'إزالة الحد',
  boundaryCreated: 'تم إنشاء حد للمجلد المقيّد.',
  boundaryRemoved: 'تمت إزالة حد المجلد المقيّد.',
  createWorkflow: 'إنشاء سير العمل الافتراضي',
  workflowCreated: 'تم إنشاء سير العمل الافتراضي.',
  noWorkflows: 'لم تتم تهيئة سير عمل لمحرك الأقراص هذا.',
  noActivity: 'لا يوجد نشاط لمحرك الأقراص بعد.',
  activityAccess: 'تم تغيير الوصول',
  activityContent: 'نشاط المحتوى',
  activityWorkflow: 'نشاط سير العمل',
  activityPublish: 'نشاط النشر',
  activityGovernance: 'تم تحديث إعدادات محرك الأقراص',
  activityRecovery: 'نشاط الاسترداد',
  activitySecurity: 'نشاط الأمان',
  activityAutomation: 'نشاط الوكيل',
  activityDrive: 'تم تحديث محرك الأقراص',
  policyUpdated: 'تم تحديث سياسة محرك الأقراص.',
  settingsFailed: 'تعذّر تحديث إعدادات محرك الأقراص.',
  guestEmail: 'البريد الإلكتروني لحساب الضيف',
  addGuest: 'إضافة ضيف',
  guestAdded: 'تمت إضافة الضيف كمعلّق.',
  guestFailed: 'تعذّرت إضافة هذا الضيف. يجب أن تكون لديه هوية IMAI مسبقًا.',
  guestExpiry:
    'تنتهي صلاحية وصول الضيف افتراضيًا بعد 90 يومًا ولا تتجاوز دور المساهم.',
  recovery: 'الاسترداد',
  contentAccessRequired: 'يلزم الوصول إلى المحتوى',
  recoveryDescription:
    'عيّن مديرًا بديلًا من دون فتح محتوى محرك الأقراص أو عرضه.',
  replacementManager: 'اختر مديرًا بديلًا',
  assignManager: 'تعيين مدير',
  recoveryAssigned: 'تم تعيين المدير البديل.',
  recoveryFailed: 'تعذّر تعيين المدير البديل.',
  publishToLibrary: 'النشر إلى محرك مساحة العمل',
  publish: 'نشر',
  publishStarted: 'بدأ إنشاء النسخة المُدارة في المكتبة.',
  publishFailed: 'تعذّر نشر هذا الملف.',
  noPublishableFiles: 'لا يحتوي محرك الأقراص هذا على ملفات قابلة للنشر.',
  libraryCuratorRequired: 'يلزم دور مدير محتوى المكتبة للنشر.',
} satisfies DriveManagementCopy;

export const deDriveManagementCopy = {
  description: 'Geteilte Dateien für Teams, Projekte und Mitwirkende.',
  createDrive: 'Ablage erstellen',
  createDriveTitle: 'Geteilte Ablage erstellen',
  driveName: 'Name der Ablage',
  driveNameHint:
    'Ablagenamen sind eindeutig und werden zum lesbaren Ordnerpfad.',
  driveNameTaken: 'Eine Ablage namens „{name}“ gibt es bereits.',
  driveNameReserved: '„{name}“ ist reserviert. Wähle einen anderen Namen.',
  driveNameInvalid: 'Verwende Buchstaben oder Ziffern im Ablagenamen.',
  driveNameSuggestions: 'Verfügbar:',
  driveDescription: 'Beschreibung (optional)',
  governance: 'Ablage für',
  curatorOptIn: 'Mich zum Bibliotheksmanager machen',
  workspace: 'Workspace',
  teamGoverned: 'Team',
  projectGoverned: 'Projekt',
  selectTeam: 'Team auswählen',
  selectProject: 'Projekt auswählen',
  projectVisibility: 'Projektsichtbarkeit',
  assignedMembers: 'Zugewiesene Mitglieder',
  workspaceVisible: 'Im Workspace sichtbar',
  customAudience: 'Benutzerdefinierte Zielgruppe',
  primaryProjectDrive: 'Als primäre Projektablage festlegen',
  create: 'Erstellen',
  cancel: 'Abbrechen',
  loading: 'Ablagen werden geladen…',
  emptyTitle: 'Keine Ablagen verfügbar',
  emptyDescription:
    'Erstelle eine geteilte Ablage oder bitte einen Manager um Zugriff.',
  openDrive: 'Ablage öffnen',
  settings: 'Ablageeinstellungen',
  members: 'Mitglieder',
  links: 'Verknüpfungen',
  permissions: 'Berechtigungen',
  workflow: 'Workflow',
  activity: 'Aktivität',
  storage: 'Speicher',
  role: 'Rolle',
  status: 'Status',
  usage: 'Belegter Speicher',
  restrictedFolders: 'Eingeschränkte Ordner',
  guestManagement: 'Gastverwaltung',
  createFailed: 'Die Ablage konnte nicht erstellt werden.',
  addMember: 'Mitglied hinzufügen',
  removeMember: 'Entfernen',
  selectMember: 'Workspace-Mitglied auswählen',
  noDirectMembers: 'Noch keine direkten Mitglieder.',
  unknownMember: 'Unbekanntes Mitglied',
  loadMoreMembers: 'Weitere Mitglieder laden',
  memberAdded: 'Mitglied hinzugefügt.',
  memberRemoved: 'Mitglied entfernt.',
  membersFailed: 'Die Ablagemitglieder konnten nicht aktualisiert werden.',
  managerOnly: 'Nur Ablagemanager können Mitglieder verwalten.',
  linkType: 'Verknüpfungstyp',
  selectLinkTarget: 'Team oder Projekt auswählen',
  addLink: 'Verknüpfung hinzufügen',
  removeLink: 'Entfernen',
  noLinks: 'Diese Ablage ist mit keinem Team oder Projekt verknüpft.',
  unknownLink: 'Nicht verfügbarer Kontext',
  linkAdded: 'Ablageverknüpfung hinzugefügt.',
  linkRemoved: 'Ablageverknüpfung entfernt.',
  linksFailed: 'Die Ablageverknüpfungen konnten nicht aktualisiert werden.',
  viewerDownloads: 'Downloads für Betrachter erlauben',
  publicLinksWorkspacePolicy:
    'Öffentliche Links unterliegen weiterhin der Richtlinie des Workspace-Eigentümers.',
  selectFolder: 'Ordner auswählen',
  restrict: 'Einschränken',
  removeRestriction: 'Grenze entfernen',
  boundaryCreated: 'Grenze für eingeschränkten Ordner erstellt.',
  boundaryRemoved: 'Grenze für eingeschränkten Ordner entfernt.',
  createWorkflow: 'Standardworkflow erstellen',
  workflowCreated: 'Standardworkflow erstellt.',
  noWorkflows: 'Für diese Ablage ist kein Workflow eingerichtet.',
  noActivity: 'Noch keine Ablageaktivität.',
  activityAccess: 'Zugriff geändert',
  activityContent: 'Inhaltsaktivität',
  activityWorkflow: 'Workflow-Aktivität',
  activityPublish: 'Veröffentlichungsaktivität',
  activityGovernance: 'Ablageeinstellungen aktualisiert',
  activityRecovery: 'Wiederherstellungsaktivität',
  activitySecurity: 'Sicherheitsaktivität',
  activityAutomation: 'Agentenaktivität',
  activityDrive: 'Ablage aktualisiert',
  policyUpdated: 'Ablagerichtlinie aktualisiert.',
  settingsFailed: 'Die Ablageeinstellungen konnten nicht aktualisiert werden.',
  guestEmail: 'E-Mail-Adresse des Gastkontos',
  addGuest: 'Gast hinzufügen',
  guestAdded: 'Gast als Kommentator hinzugefügt.',
  guestFailed:
    'Dieser Gast konnte nicht hinzugefügt werden. Eine IMAI-Identität muss bereits vorhanden sein.',
  guestExpiry:
    'Gastzugriff läuft standardmäßig nach 90 Tagen ab und ist auf Mitwirkender begrenzt.',
  recovery: 'Wiederherstellung',
  contentAccessRequired: 'Inhaltszugriff erforderlich',
  recoveryDescription:
    'Weise einen Ersatzmanager zu, ohne Ablageinhalte zu öffnen oder anzusehen.',
  replacementManager: 'Ersatzmanager auswählen',
  assignManager: 'Manager zuweisen',
  recoveryAssigned: 'Ersatzmanager zugewiesen.',
  recoveryFailed: 'Der Ersatzmanager konnte nicht zugewiesen werden.',
  publishToLibrary: 'In Workspace-Ablage veröffentlichen',
  publish: 'Veröffentlichen',
  publishStarted: 'Verwaltete Bibliothekskopie wurde gestartet.',
  publishFailed: 'Diese Datei konnte nicht veröffentlicht werden.',
  noPublishableFiles: 'Diese Ablage enthält keine veröffentlichbaren Dateien.',
  libraryCuratorRequired:
    'Zum Veröffentlichen ist die Rolle Inhaltsmanager der Bibliothek erforderlich.',
} satisfies DriveManagementCopy;
